/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '@/components/Icon';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type DialogContentProps,
} from '@/design-system';
import { cn } from '@/lib/cn';

export interface ShellDialogProps {
  /** Called when the dialog asks to close: Escape, the scrim, the close button. */
  onClose: () => void;
  /** Dialog title (18/500). */
  title: ReactNode;
  /** A sentence under the title. */
  description?: ReactNode;
  /** Glyph in the accent tile beside the title. */
  icon: IconName;
  /** Floating width: `sm` 420, `md` 560 (default), `lg` 760, `xl` 1040. */
  size?: DialogContentProps['size'];
  /** Action row, pinned to the bottom (stacks full-width below 481). */
  footer?: ReactNode;
  /** Edge-to-edge body for content that brings its own padding or dividers. */
  flush?: boolean;
  /** Extra class on the dialog surface (a width override, an area hook). */
  className?: string;
  /** Extra class on the body. */
  bodyClassName?: string;
  /** Keep the dialog open on Escape and outside clicks (an action is in flight). */
  busy?: boolean;
  children: ReactNode;
}

/**
 * The shell's dialogs (Settings, Profile, About, What's new, Resource links, Feedback, the VIN
 * scanner) on the kit Dialog: a floating card from 600px wide and 561px tall, a full-screen sheet
 * below either, the header and footer pinned while the body scrolls. Only the header is the
 * shell's own: an accent icon tile beside the title, and a close button.
 */
export function ShellDialog({
  onClose,
  title,
  description,
  icon,
  size = 'md',
  footer,
  flush = false,
  className,
  bodyClassName,
  busy = false,
  children,
}: ShellDialogProps) {
  const { t } = useTranslation();
  const guard = (event: Event) => {
    if (busy) event.preventDefault();
  };
  return (
    // Always open while mounted (the caller unmounts it), so a change can only be a request to close.
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        size={size}
        className={cn('shell-dlg', className)}
        // Radix warns about a dialog without a description unless told there is none on purpose.
        {...(description ? {} : { 'aria-describedby': undefined })}
        onEscapeKeyDown={guard}
        onPointerDownOutside={guard}
      >
        <DialogHeader className="shell-dlg-head">
          <span className="shell-dlg-ic" aria-hidden="true">
            <Icon name={icon} size={20} />
          </span>
          <div className="shell-dlg-titles">
            <DialogTitle className="shell-dlg-title">{title}</DialogTitle>
            {description && (
              <DialogDescription className="shell-dlg-sub">{description}</DialogDescription>
            )}
          </div>
          <DialogClose asChild>
            <button type="button" className="iclose" aria-label={t('common.close')} disabled={busy}>
              <Icon name="close" size={18} />
            </button>
          </DialogClose>
        </DialogHeader>
        <DialogBody flush={flush} className={cn('shell-dlg-body', bodyClassName)}>
          {children}
        </DialogBody>
        {footer && <DialogFooter className="shell-dlg-foot">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
